import { useState } from 'react'
import { Modal, useModals } from 'almanac-ui/Modal'
import { FromURL } from '../SourceMaterialModal/ContentSourcePicker/FromURL'
import { FromText } from '../SourceMaterialModal/ContentSourcePicker/FromText'
import { FromFile } from '../SourceMaterialModal/ContentSourcePicker/FromFile'
import { MainSelectionScreen } from './MainSelectionScreen'
import { useSourceMaterialModal } from './useSourceMaterialModal'
import { SCREEN_TITLES, type ScreenType } from './constants'
import { Checkbox } from 'blaze-ui/Checkbox/Checkbox'
import type {
  KnowledgeBaseSourceMaterial,
  PlainText,
  Website,
  AlmanacFileSource,
  SourceMaterialsMediaObject,
} from 'types'
import Styles from './SourceMaterialModalV2.module.scss'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'

export interface SourceMaterialModalV2Props {
  onHandleSourceMaterialSuccess?: (sourceMaterial: KnowledgeBaseSourceMaterial) => void
  onSuccess?: (source: PlainText | Website | AlmanacFileSource | SourceMaterialsMediaObject) => void
  brandKitId?: number
  initialScreen?: ScreenType
}

export const SourceMaterialModalV2 = ({
  onHandleSourceMaterialSuccess,
  onSuccess,
  initialScreen,
}: SourceMaterialModalV2Props) => {
  const isSystemRedesignEnabled = useIsSystemRedesign()
  const { closeModal } = useModals()
  const [currentScreen, setCurrentScreen] = useState<ScreenType | null>(initialScreen || null)
  const [showError, setShowError] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const [scrapeImages, setScrapeImages] = useState(true)

  const { url, setUrl, text, setText, fileContent, setFileContent, isValid, isSubmitting, submit } =
    useSourceMaterialModal({
      currentScreen,
      onHandleSourceMaterialSuccess,
      onSuccess,
      onError: () => {
        setShowError(true)
        setUrl('')
      },
    })

  const handleBack = () => {
    setCurrentScreen(null)
    setShowError(false)
  }

  const handleAnalyze = async () => {
    if (!isValid) {
      setShowError(true)
      return
    }

    await submit(currentScreen, { url, text, file, fileContent, scrapeImages })
  }

  const getTitle = () => {
    if (!currentScreen) return 'Add more context'
    return (
      <>
        <Modal.BackButton onPress={handleBack} />
        {SCREEN_TITLES[currentScreen]}
      </>
    )
  }

  return (
    <Modal.Root size="sm" blaze bodyClassName={Styles.modalBody}>
      <Modal.Header title={getTitle()} onClose={closeModal} />

      <Modal.Content withoutFooter={!currentScreen}>
        {!currentScreen && <MainSelectionScreen onSelect={setCurrentScreen} />}

        {currentScreen === 'website' && (
          <div className={Styles.screenContent}>
            <FromURL
              url={url}
              onChange={(newUrl) => {
                setUrl(newUrl)
              }}
              showError={showError}
              autoFocus
              isDisabled={isSubmitting}
            >
              <div className={Styles.scrapeImagesCheckbox}>
                <Checkbox isSelected={scrapeImages} onChange={setScrapeImages} isDisabled={isSubmitting}>
                  Extract images and include them in your media library
                </Checkbox>
              </div>
            </FromURL>
          </div>
        )}

        {currentScreen === 'text' && (
          <div className={Styles.screenContent}>
            <FromText sampleText={text} showError={showError} onChange={setText} autoFocus />
          </div>
        )}

        {currentScreen === 'file' && (
          <div className={Styles.screenContent}>
            <FromFile
              onChange={(content, selectedFile) => {
                setFile(selectedFile)
                setFileContent(content || '')
              }}
              showError={showError}
            />
          </div>
        )}
      </Modal.Content>

      {currentScreen && (
        <Modal.Footer blaze>
          {isSystemRedesignEnabled && (
            <Modal.FooterContent slot="left">
              <Modal.FooterButton variant="tertiary" onPress={handleBack} isDisabled={isSubmitting}>
                Back
              </Modal.FooterButton>
            </Modal.FooterContent>
          )}
          <Modal.FooterContent slot="right">
            <Modal.FooterButton variant="primary" onPress={handleAnalyze} isDisabled={!isValid || isSubmitting}>
              {isSubmitting ? 'Analyzing...' : 'Analyze'}
            </Modal.FooterButton>
          </Modal.FooterContent>
        </Modal.Footer>
      )}
    </Modal.Root>
  )
}
